import {
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { ImgCategory } from '../model/img-category';
import { ColorService } from '../../shared/services/color.service';
import { UserCacheService } from 'src/app/xtra/user-cache/user-cache-service';
import { ImgDbService } from '../services/img-db.service';
import { ApplicationLockService } from 'src/app/shared/services/application-lock.service';
import {
    ConfirmationDialogComponent,
    ConfirmationDialogData,
} from '../../shared/components/confirmation-modal/confirmation-dialog.component';
import { SortingKey } from '../img-db.component';
import { AnnotationStateService } from '../services/annotation-state.service';
import { ImagesStorageService } from '../services/images-storage.service';

export type RadioButtons = 'images' | 'labels';
export type SortingOptions = 'A-Z' | 'Z-A' | '0-9' | '9-0';
export const COUNTING_GROUP_SELECTION_SAVE_LOCATION_PREFIX: string =
    'countingGroupOf';

@Component({
    selector: 'app-img-db-category-manager',
    templateUrl: './img-db-category-manager.component.html',
    styleUrls: ['./img-db-category-manager.component.scss'],
    standalone: false,
})
export class ImgDbCategoryManagerComponent
    implements OnInit, OnChanges, OnChanges
{
    readonly appLockMessage: string = 'appLock.categoryUpdate';

    @Input() imageDatabaseId: string;
    @Input() canEdit: boolean;
    @Input() categories: ImgCategory[];
    @Input() selectedCategoryNames: string[];
    @Input() numberOfUncategorizedImages: number;
    @Input() isEmptyCategorySelected: boolean;
    @Input() isDbOfTraining: boolean = false;
    @Input() mode: string;
    @Input() selectedSorting: SortingOptions;
    @Input() fetchOnInit: boolean;

    @Output() actionTaken = new EventEmitter<CategoryManagerAction>();

    @ViewChild('labelName') lblName: ElementRef;
    @ViewChild('categoryRenameInput') categoryRenameInput: ElementRef;
    public categoryInput = new FormControl('');

    availableOptions: RadioButtons[] = ['labels', 'images'];
    selectedOption: RadioButtons = 'images';
    sortingOptions: SortingOptions[] = ['A-Z', 'Z-A', '0-9', '9-0'];
    selectedSortingOption: FormControl<SortingOptions>;
    hoveredCategoryIndex: number | null = null;
    showSpinner: boolean = false;
    categoryMenuOpen: boolean = false;
    menuForIndex: number = -1;
    categoryIndexForRenaming: number = -1;
    currentRenamingCategoryName: string = null;
    selectedCategoryNameForDrawing: string = null;
    inputFilteredCategories: ImgCategory[] = [];

    constructor(
        private dialog: MatDialog,
        public translate: TranslateService,
        private colorService: ColorService,
        private userCacheService: UserCacheService,
        private imageDbService: ImgDbService,
        private blockerService: ApplicationLockService,
        private annotationsStateService: AnnotationStateService,
        private imagesStorage: ImagesStorageService
    ) {}

    ngOnInit(): void {
        this.selectedSortingOption = new FormControl<SortingOptions>(
            this.selectedSorting
        );
        this.userCacheService
            .loadData(
                `${COUNTING_GROUP_SELECTION_SAVE_LOCATION_PREFIX}${this.imageDatabaseId}`
            )
            .subscribe((savedSelection: { selected: RadioButtons }) => {
                if (savedSelection) {
                    this.selectedOption = savedSelection.selected;
                }
                if (this.fetchOnInit) {
                    this.onSortingChange();
                }
            });
        if (!this.canEdit) {
            this.categoryInput.disable({ onlySelf: true, emitEvent: false });
        }
        this.setUpInputFilteredCategories();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes && changes.categories) {
            this.showSpinner = false;
            if (this.canEdit) {
                this.categoryInput.enable({ emitEvent: false });
            }
        }
        if (changes.canEdit) {
            this.categoryInput.disable({ onlySelf: true, emitEvent: false });
        }
        this.setUpInputFilteredCategories();
    }

    isCategoryActive(category: ImgCategory): boolean {
        return (
            (this.mode === 'overview' &&
                this.selectedCategoryNames.includes(category.name)) ||
            (this.mode === 'annotate' &&
                this.selectedCategoryNameForDrawing === category.name)
        );
    }

    onClickCategory(category: ImgCategory) {
        if (this.categoryIndexForRenaming < 0) {
            if (this.mode === 'annotate') {
                if (
                    !category ||
                    this.selectedCategoryNameForDrawing === category.name
                ) {
                    this.selectedCategoryNameForDrawing = null;
                    this.actionTaken.emit(
                        new CategoryManagerAction(
                            CategoryManagerActionType.CATEGORY_CLICKED,
                            new CategoryClickedData(null)
                        )
                    );
                } else {
                    this.selectedCategoryNameForDrawing = category.name;
                    this.actionTaken.emit(
                        new CategoryManagerAction(
                            CategoryManagerActionType.CATEGORY_CLICKED,
                            new CategoryClickedData(category)
                        )
                    );
                }
            } else {
                this.actionTaken.emit(
                    new CategoryManagerAction(
                        CategoryManagerActionType.CATEGORY_CLICKED,
                        new CategoryClickedData(category)
                    )
                );
            }
        }
    }

    onClickEmptyCategory() {
        if (this.mode === 'overview') {
            this.actionTaken.emit(
                new CategoryManagerAction(
                    CategoryManagerActionType.EMPTY_CATEGORY_SELECTED
                )
            );
        }
    }

    onSortingChange() {
        this.showSpinner = true;
        this.categoryInput.disable({ emitEvent: false });
        const option: SortingOptions = this.selectedSortingOption.value;
        let suffix: string = '';
        if (option.startsWith('0') || option.startsWith('9')) {
            suffix = this.selectedOption;
        }
        this.actionTaken.emit(
            new CategoryManagerAction(
                CategoryManagerActionType.CATEGORY_SORTING_CHANGED,
                new CategorySortingChangedData({ option, suffix } as SortingKey)
            )
        );
    }

    onInputChange(event: KeyboardEvent) {
        event.stopPropagation();
        if (event.key === 'Enter') {
            const categoryForRenaming =
                this.categories[this.categoryIndexForRenaming];

            if (this.currentRenamingCategoryName === categoryForRenaming.name) {
                this.resetRenaming();
                return;
            }

            this.blockerService.setApplicationLock(this.appLockMessage);
            this.annotationsStateService
                .saveCommands(true, this.imagesStorage.selectedImage$.value?.id)
                .subscribe((_) => {
                    const conflictingCategory = this.categories.find(
                        (category: ImgCategory) =>
                            category.name === this.currentRenamingCategoryName
                    );
                    if (conflictingCategory) {
                        if (conflictingCategory.id !== categoryForRenaming.id) {
                            this.blockerService.releaseApplicationLock();
                            this.openMergeDialog(
                                new CategoryMergeParameter(
                                    categoryForRenaming.id,
                                    conflictingCategory.id
                                ),
                                conflictingCategory.name
                            );
                        }
                    } else {
                        this.renameCategory(categoryForRenaming.id);
                    }
                    this.resetRenaming();
                });
        }
        if (event.key === 'Escape') {
            this.resetRenaming();
        }
    }

    resetRenaming() {
        this.categoryIndexForRenaming = -1;
        this.currentRenamingCategoryName = null;
    }

    renameCategory(categoryId: string) {
        const newName = this.currentRenamingCategoryName;
        this.imageDbService
            .updateCategory(this.imageDatabaseId, categoryId, newName)
            .subscribe({
                next: () => {
                    this.actionTaken.emit(
                        new CategoryManagerAction(
                            CategoryManagerActionType.CATEGORY_RENAMED,
                            new CategoryRenameData(
                                this.categories.find(
                                    (category) => category.id === categoryId
                                ).name,
                                newName
                            )
                        )
                    );
                },
                error: (errorResponse) => {
                    this.blockerService.releaseApplicationLock();
                    if (errorResponse.status === '409') {
                        this.openMergeDialog(
                            new CategoryMergeParameter(
                                errorResponse.sourceId,
                                errorResponse.targetId
                            ),
                            newName
                        );
                    } else {
                        this.actionTaken.emit(
                            new CategoryManagerAction(
                                CategoryManagerActionType.UPDATE_STATE
                            )
                        );
                    }
                },
            });
    }

    onAfterClosed(data: CategoryMergeParameter) {
        if (data) {
            this.confirmMerge(data.sourceCategoryId, data.targetCategoryId);
        }
    }

    openMergeDialog(
        categoryMergeParameter: CategoryMergeParameter,
        targetCategoryName: string
    ) {
        this.dialog
            .open<
                ConfirmationDialogComponent,
                ConfirmationDialogData,
                CategoryMergeParameter
            >(ConfirmationDialogComponent, {
                data: {
                    headerTranslationKey:
                        'imgDb.labelManager.mergeDialog.header',
                    confirmationQuestion: {
                        translationKey:
                            'imgDb.labelManager.mergeDialog.question',
                        interpolateParameters: {
                            categoryName: targetCategoryName,
                        },
                    },
                    confirmButtonTranslationKey:
                        'imgDb.labelManager.mergeDialog.confirmText',
                    rejectButtonTranslationKey:
                        'imgDb.labelManager.mergeDialog.cancelText',
                    transferData: categoryMergeParameter,
                } as ConfirmationDialogData,
                width: '550px',
            })
            .afterClosed()
            .subscribe(this.onAfterClosed.bind(this));
    }

    confirmMerge(sourceCategoryId: string, targetCategoryId: string) {
        this.blockerService.setApplicationLock(this.appLockMessage);
        this.imageDbService
            .mergeCategory(
                this.imageDatabaseId,
                sourceCategoryId,
                targetCategoryId
            )
            .subscribe({
                next: () => {
                    this.actionTaken.emit(
                        new CategoryManagerAction(
                            CategoryManagerActionType.CATEGORY_MERGED,
                            new CategoryMergeData(
                                this.categories.find(
                                    (category) =>
                                        category.id === sourceCategoryId
                                ).name,
                                sourceCategoryId,
                                targetCategoryId
                            )
                        )
                    );
                },
                error: (_) => {
                    this.actionTaken.emit(
                        new CategoryManagerAction(
                            CategoryManagerActionType.UPDATE_STATE
                        )
                    );
                },
            });
    }

    onEnterKeypressAddCategory(categoryName: string) {
        if (
            categoryName &&
            !ImgCategory.includes(this.categories, categoryName)
        ) {
            this.showSpinner = true;
            this.categoryInput.disable({ emitEvent: false });
            this.actionTaken.emit(
                new CategoryManagerAction(
                    CategoryManagerActionType.CATEGORY_CREATED,
                    new CategoryCreatedData(categoryName)
                )
            );
            this.resetForm();
        }
        this.lblName.nativeElement.focus();
    }

    resetForm() {
        this.categoryInput.reset();
        this.lblName.nativeElement.focus();
    }

    onResetFormAndInputFieldFilteredCategories() {
        // Used for delete button in input
        this.resetCategoryFilter();
        this.resetForm();
    }

    onDeleteCategoryClick(event: Event, category: ImgCategory) {
        if (category.id === undefined) {
            return;
        }
        event.stopPropagation();
        this.closeCategoryMenu();
        const categoryIdToDelete: string = category.id;
        this.openDeletionDialog(categoryIdToDelete)
            .afterClosed()
            .subscribe((confirmedCategoryIdToDelete: string) => {
                if (confirmedCategoryIdToDelete) {
                    if (category.name === this.selectedCategoryNameForDrawing) {
                        this.onClickCategory(null);
                    }
                    this.actionTaken.emit(
                        new CategoryManagerAction(
                            CategoryManagerActionType.CATEGORY_DELETED,
                            new CategoryDeletedData(confirmedCategoryIdToDelete)
                        )
                    );
                }
            });
    }

    onCategoryMenuClick(event: Event, categoryIndex: number) {
        event.stopPropagation();
        if (this.menuForIndex !== categoryIndex) {
            this.menuForIndex = categoryIndex;
            this.categoryMenuOpen = true;
        } else {
            this.closeCategoryMenu();
        }
    }

    onRename(event: Event, categoryIndex: number) {
        if (this.mode === 'annotate') {
            this.onClickCategory(null);
        }
        event.stopPropagation();
        this.closeCategoryMenu();
        this.currentRenamingCategoryName = this.categories[categoryIndex].name;
        this.categoryIndexForRenaming = categoryIndex;
        setTimeout(() => this.categoryRenameInput?.nativeElement?.focus(), 300);
    }

    onCloseCategoryMenu(event: Event) {
        event.stopPropagation();
        this.closeCategoryMenu();
    }

    closeCategoryMenu() {
        this.categoryMenuOpen = false;
        this.menuForIndex = -1;
    }

    private openDeletionDialog(categoryToDelete: string) {
        const dialogConfig = new MatDialogConfig();

        dialogConfig.width = '550px';
        dialogConfig.data = {
            headerTranslationKey: 'imgDb.labelManager.dialog.header',
            confirmationQuestion: {
                translationKey: 'imgDb.labelManager.dialog.content',
                interpolateParameters: {},
            },
            confirmButtonTranslationKey:
                'imgDb.labelManager.dialog.confirmText',
            rejectButtonTranslationKey: 'imgDb.labelManager.dialog.cancelText',
            transferData: categoryToDelete,
        } as ConfirmationDialogData;

        return this.dialog.open(ConfirmationDialogComponent, dialogConfig);
    }

    private setUpInputFilteredCategories() {
        if (!this.categoryInputIsEmpty()) {
            this.filterImgCategories(this.categories);
        } else {
            this.resetCategoryFilter();
        }
    }

    private categoryInputIsEmpty() {
        return !this.categoryInput.value?.trim();
    }

    private resetCategoryFilter() {
        // ok Name :)
        this.inputFilteredCategories = this.categories;
    }

    private filterImgCategories(categoriesToFilter: ImgCategory[]) {
        if (this.categoryInputIsEmpty()) {
            this.resetCategoryFilter();
        } else {
            // Filter is shallow copy!
            const currentInputValue: string = this.categoryInput.value
                .toLowerCase()
                .trim();
            this.inputFilteredCategories = categoriesToFilter.filter(
                (category: ImgCategory) => {
                    return category.name
                        .toLowerCase()
                        .includes(currentInputValue);
                }
            );
        }
    }

    public onKeyUpCategoryInput(event: KeyboardEvent) {
        switch (event.key) {
            case 'Enter':
                this.onEnterKeypressAddCategory(
                    this.categoryInput.value?.trim()
                );
                break;
            case 'Escape':
                this.resetCategoryFilter();
                this.categoryInput.reset();
                this.lblName.nativeElement.blur();
                break;
            default:
                this.filterImgCategories(this.categories);
                break;
        }
    }

    public getColor(category) {
        return this.colorService.getColor(category);
    }

    public onCountingGroupChange(group: RadioButtons) {
        if (group !== this.selectedOption) {
            this.userCacheService
                .saveData(
                    `${COUNTING_GROUP_SELECTION_SAVE_LOCATION_PREFIX}${this.imageDatabaseId}`,
                    { selected: group }
                )
                .subscribe();
            if (
                this.selectedSortingOption.value.startsWith('0') ||
                this.selectedSortingOption.value.startsWith('9')
            ) {
                this.selectedOption = group;
                this.onSortingChange();
            }
        }
    }
}

export class CategoryMergeParameter {
    constructor(
        public sourceCategoryId: string,
        public targetCategoryId: string
    ) {}
}

export class CategoryManagerAction {
    constructor(
        public type: CategoryManagerActionType,
        public parameter?:
            | CategoryCreatedData
            | CategoryClickedData
            | CategoryDeletedData
            | CategorySortingChangedData
            | CategoryRenameData
            | CategoryMergeData
    ) {}
}

export enum CategoryManagerActionType {
    CATEGORY_CREATED = 'CATEGORY_CREATED',
    CATEGORY_CLICKED = 'CATEGORY_CLICKED',
    CATEGORY_DELETED = 'CATEGORY_DELETED',
    EMPTY_CATEGORY_SELECTED = 'EMPTY_CATEGORY_SELECTED',
    CATEGORY_SORTING_CHANGED = 'CATEGORY_SORTING_CHANGED',
    CATEGORY_RENAMED = 'CATEGORY_RENAMED',
    CATEGORY_MERGED = 'CATEGORY_MERGED',
    UPDATE_STATE = 'UPDATE_STATE',
}

export class CategoryCreatedData {
    constructor(public categoryName: string) {}
}

export class CategoryClickedData {
    constructor(public category: ImgCategory | null) {}
}

export class CategoryDeletedData {
    constructor(public categoryId: string) {}
}

export class CategorySortingChangedData {
    constructor(public sortingKey: SortingKey) {}
}

export class CategoryRenameData {
    constructor(
        public oldName: string,
        public newName: string
    ) {}
}

export class CategoryMergeData {
    constructor(
        public oldName: string,
        public oldId: string,
        public newId: string
    ) {}
}
